import { BaseApiService } from './BaseApiService';

export interface BulkProduct {
  id: number;
  name: string;
  sku_number: string;
  vendor?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateBulkProductData {
  name: string;
  sku_number: string;
  vendor?: string;
}

export interface UpdateBulkProductData {
  name?: string;
  sku_number?: string;
  vendor?: string;
}

class BulkProductService extends BaseApiService {
  async getAllBulkProducts(): Promise<BulkProduct[]> {
    const response = await this.get<{ success: boolean; data: BulkProduct[] }>('/bulk-products');
    return response.data || [];
  }

  async getBulkProductsPaginated(
    page = 1,
    pageSize = 20
  ): Promise<{
    bulkProducts: BulkProduct[];
    pagination: {
      total: number;
      totalPages: number;
      currentPage: number;
      pageSize: number;
    };
  }> {
    const params = { page: page.toString(), pageSize: pageSize.toString() };
    const response = await this.get<{
      success: boolean;
      data: BulkProduct[];
      pagination: {
        total: number;
        totalPages: number;
        currentPage: number;
        pageSize: number;
      };
    }>('/bulk-products', params);

    return {
      bulkProducts: response.data || [],
      pagination: response.pagination || { total: 0, totalPages: 0, currentPage: 1, pageSize: 20 },
    };
  }

  async searchBulkProducts(searchTerm: string): Promise<BulkProduct[]> {
    const params = { search: searchTerm };
    const response = await this.get<{ success: boolean; data: BulkProduct[] }>(
      '/bulk-products',
      params
    );
    return response.data || [];
  }

  async searchBulkProductsPaginated(
    searchTerm: string,
    page = 1,
    pageSize = 20
  ): Promise<{
    bulkProducts: BulkProduct[];
    pagination: {
      total: number;
      totalPages: number;
      currentPage: number;
      pageSize: number;
    };
  }> {
    const params = {
      search: searchTerm,
      page: page.toString(),
      pageSize: pageSize.toString(),
    };
    const response = await this.get<{
      success: boolean;
      data: BulkProduct[];
      pagination: {
        total: number;
        totalPages: number;
        currentPage: number;
        pageSize: number;
      };
    }>('/bulk-products', params);

    return {
      bulkProducts: response.data || [],
      pagination: response.pagination || { total: 0, totalPages: 0, currentPage: 1, pageSize: 20 },
    };
  }

  async getBulkProductById(id: number): Promise<BulkProduct> {
    const response = await this.get<{ success: boolean; data: BulkProduct }>(
      `/bulk-products/${id}`
    );
    if (!response.data) {
      throw new Error('Bulk product not found');
    }
    return response.data;
  }

  async createBulkProduct(data: CreateBulkProductData): Promise<BulkProduct> {
    const response = await this.post<{ success: boolean; data: BulkProduct }>(
      '/bulk-products',
      data
    );
    if (!response.data) {
      throw new Error('Failed to create bulk product');
    }
    return response.data;
  }

  async updateBulkProduct(id: number, data: UpdateBulkProductData): Promise<BulkProduct> {
    const response = await this.put<{ success: boolean; data: BulkProduct }>(
      `/bulk-products/${id}`,
      data
    );
    if (!response.data) {
      throw new Error('Failed to update bulk product');
    }
    return response.data;
  }

  async deleteBulkProduct(id: number): Promise<void> {
    await this.delete<{ success: boolean; message: string }>(`/bulk-products/${id}`);
  }
}

export const bulkProductService = new BulkProductService();
